import { z } from 'zod'
import { channelSchema, localeSchema, sourceRefSchema, toolCallRecordSchema } from './session'

/** Inbound request to POST /api/v1/chat */
export const chatRequestSchema = z.object({
  sessionId: z.string(),
  channel: channelSchema,
  locale: localeSchema.optional(),
  message: z.string().min(1).max(4000),
  /** Optional current-camera-frame context (never persisted, see FR-34) */
  imageDataUrl: z.string().startsWith('data:image/').optional(),
  showcaseContextId: z.string().optional(),
  /** Mehrere Showcases im Kamerabild (ganze Vitrine): K4ia sagt, was zu sehen ist, und fragt nach. */
  showcaseContextIds: z.array(z.string()).max(6).optional(),
  /**
   * `tag`: Der Besucher hat den NFC-Tag am Showcase gescannt — K4ia begrüßt
   * kurz und erklärt ihn gleich. `tour-walk`: Rundgang, K4ia beschreibt den
   * Weg zu `showcaseContextId`. `tour-arrived`: er ist da, K4ia erklärt.
   * `tour-end`: der Rundgang ist zu Ende.
   */
  entry: z.enum(['tag', 'tour-walk', 'tour-arrived', 'tour-end']).optional(),
})
export type ChatRequest = z.infer<typeof chatRequestSchema>

/**
 * Server-Sent-Event payloads streamed back from /api/v1/chat.
 * One event type per SSE `event:` line; `data:` is this schema JSON-encoded.
 */
/**
 * Verbrauchszahlen eines Modellaufrufs. Zwischengespeicherte Eingabe-Token
 * werden getrennt geführt, weil sie deutlich weniger kosten — sie in
 * `inputTokens` einzurechnen würde die Kostenschätzung systematisch zu hoch
 * ausfallen lassen.
 */
export const usageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheReadInputTokens: z.number().int().nonnegative().default(0),
})
export type Usage = z.infer<typeof usageSchema>

export const chatStreamEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('token'), delta: z.string() }),
  z.object({
    type: z.literal('tool_call'),
    id: z.string(),
    name: z.string(),
    input: z.record(z.unknown()),
    requiresConfirmation: z.boolean(),
  }),
  z.object({
    type: z.literal('tool_result'),
    id: z.string(),
    output: z.record(z.unknown()),
  }),
  z.object({ type: z.literal('source'), source: sourceRefSchema }),
  z.object({
    type: z.literal('suggestion'),
    suggestions: z.array(z.string()),
  }),
  z.object({
    type: z.literal('done'),
    messageId: z.string(),
    generalKnowledge: z.boolean(),
    toolCalls: z.array(toolCallRecordSchema),
    latencyMs: z.number().int(),
    /**
     * Verbrauch dieses Zugs, über alle Werkzeugrunden summiert. Grundlage für
     * die Kostengrenzen (SPEC „Kostenlimits pro Session und pro Tag") und für
     * die Tokenzahlen im Admin. Fehlt bei Kanälen ohne Modellaufruf.
     */
    usage: usageSchema.optional(),
    /**
     * Wo die Zeit bis zum ersten Wort hingegangen ist, in Millisekunden:
     * `vorbereitung` (Verlauf, Konfiguration, Kostengrenze im Durable
     * Object), `suche` (Wissenssuche vor der ersten Modellrunde, läuft zum
     * Teil parallel zur Vorbereitung; aufgeteilt in `einbettung`,
     * `vektorindex`, `datenbank`), `erstesWort` (vom Start der Antwort
     * bis zum ersten Zeichen des Modells). Für Messungen gegen Test und
     * Production (`scripts/load-test.ts`); die Apps ignorieren es.
     */
    timingMs: z
      .object({
        vorbereitung: z.number().int().nonnegative(),
        suche: z.number().int().nonnegative(),
        einbettung: z.number().int().nonnegative(),
        vektorindex: z.number().int().nonnegative(),
        datenbank: z.number().int().nonnegative(),
        erstesWort: z.number().int().nonnegative(),
      })
      .partial()
      .optional(),
  }),
  z.object({
    type: z.literal('error'),
    code: z.enum(['rate_limited', 'cost_limited', 'provider_unavailable', 'internal']),
    message: z.string(),
    /** true when the app should fall back from voice to text (FR degrade path) */
    degradeToText: z.boolean().default(false),
  }),
])
export type ChatStreamEvent = z.infer<typeof chatStreamEventSchema>

export const toolConfirmationRequestSchema = z.object({
  sessionId: z.string(),
  toolCallId: z.string(),
  confirmed: z.boolean(),
  /**
   * Vom Besucher in einer Karte bearbeitete Eingaben (Kontaktkarte bei
   * `create_followup`). Sie ersetzen die Felder des Aufrufs, bevor das Werkzeug
   * läuft — der Besucher sieht und tippt selbst, was hinausgeht.
   */
  input: z.record(z.string(), z.unknown()).optional(),
})
export type ToolConfirmationRequest = z.infer<typeof toolConfirmationRequestSchema>
